import { AiPromptsManager } from "@/components/ai/ai-prompts-manager";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { PromptScope } from "@/services/supabase/ai-prompts";
import { useLicense } from '@/hooks/useLicense';
import React, { useEffect, useState } from 'react';

interface ChapterDialogProps {
  onSave: (data: { name: string; description: string; outline?: string }) => void;
  onClose: () => void;
  data: {
    defaultValues?: {
      name: string;
      description: string;
      outline?: string;
    };
    isEditing?: boolean;
    chapterId?: number; // Added chapterId for AI Prompts
  };
}

export const ChapterDialog: React.FC<ChapterDialogProps> = ({
  onSave,
  onClose,
  data
}) => {
  const defaultValues = data?.defaultValues || { name: '', description: '', outline: '' };
  const isEditing = data?.isEditing || false;
  const chapterId = data?.chapterId;
  const { hasAIFeatures } = useLicense();

  const [name, setName] = useState(defaultValues.name);
  const [description, setDescription] = useState(defaultValues.description);
  const [outline, setOutline] = useState(defaultValues.outline || '');
  const [activeTab, setActiveTab] = useState("details");

  // Reset form state when dialog opens with new data
  useEffect(() => {
    setName(defaultValues.name);
    setDescription(defaultValues.description);
    setOutline(defaultValues.outline || '');
  }, [defaultValues.name, defaultValues.description, defaultValues.outline]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ name, description, outline });
  };

  return (
    <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
      <DialogHeader className="pb-4 flex-none">
        <DialogTitle className="text-xl font-semibold text-foreground">
          {isEditing ? 'Edit Chapter' : 'Create Chapter'}
        </DialogTitle>
        <p className="text-sm text-muted-foreground mt-2">
          {isEditing
            ? 'Update your chapter details and AI settings'
            : 'Create a new chapter with description and outline'
          }
        </p>
      </DialogHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className={`grid w-full ${hasAIFeatures ? 'grid-cols-2' : 'grid-cols-1'} mb-4`}>
          <TabsTrigger value="details">Chapter Details</TabsTrigger>
          {hasAIFeatures && <TabsTrigger value="ai" disabled={!isEditing}>AI Settings</TabsTrigger>}
        </TabsList>

        <TabsContent value="details" className="flex-1 min-h-0 overflow-y-auto pr-1">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-6">
              {/* Chapter Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-foreground">
                  Chapter Name
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Chapter 1: The Beginning"
                  className="w-full h-11 text-base"
                  required
                />
              </div>

              {/* Description Section */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium text-foreground">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what happens in this chapter..."
                  className="min-h-[120px] resize-none text-base leading-relaxed"
                />
              </div>

              {/* Outline Section */}
              <div className="space-y-2">
                <Label htmlFor="outline" className="text-sm font-medium text-foreground">
                  Chapter Outline
                </Label>
                <Textarea
                  id="outline"
                  value={outline}
                  onChange={(e) => setOutline(e.target.value)}
                  placeholder="Write your detailed chapter outline here..."
                  className="min-h-[200px] resize-none text-base leading-relaxed"
                />
              </div>
            </div>

            <DialogFooter className="pt-4 sticky bottom-0 bg-background pb-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="min-w-[100px]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="min-w-[120px]"
                disabled={!name.trim()}
              >
                {isEditing ? 'Update Chapter' : 'Create Chapter'}
              </Button>
            </DialogFooter>
          </form>
        </TabsContent>

        {hasAIFeatures && (
        <TabsContent value="ai" className="mt-4">
          {data?.chapterId ? (
            <AiPromptsManager
              scope={PromptScope.Chapter}
              contextId={data.chapterId}
            />
          ) : (
            <div className="text-center p-8 border border-dashed rounded-lg text-muted-foreground">
              Please save the chapter first to add AI prompts.
            </div>
          )}
        </TabsContent>
        )}
      </Tabs>
    </DialogContent>
  );
};
